import { Subject, filter } from 'rxjs';
/**
 * Event Bus for domain events using RxJS
 * Обеспечивает слабую связанность между компонентами
 */
export class EventBus {
    eventStream;
    eventHistory = [];
    maxHistorySize = 1000;
    handlers = new Map();
    simpleHandlers = new Map();
    constructor() {
        this.eventStream = new Subject();
        // Подписка на все события для истории
        this.eventStream.subscribe(event => {
            this.addToHistory(event);
            this.executeHandlers(event);
        });
    }
    /**
     * Публикация события
     */
    publish(event) {
        this.eventStream.next(event);
    }
    /**
     * Публикация нескольких событий
     */
    publishMany(events) {
        events.forEach(event => this.publish(event));
    }
    /**
     * Получение Observable для конкретного типа события
     */
    on(eventType) {
        return this.eventStream.pipe(filter((event) => event.constructor.name === eventType.name));
    }
    /**
     * Получение всех событий
     */
    onAll() {
        return this.eventStream.asObservable();
    }
    /**
     * Выполнение зарегистрированных обработчиков
     */
    async executeHandlers(event) {
        const eventTypeName = event.constructor.name;
        const handlers = this.handlers.get(eventTypeName);
        if (!handlers)
            return;
        for (const handler of handlers) {
            try {
                await handler(event);
            }
            catch (error) {
                console.error(`Error executing handler for ${eventTypeName}:`, error);
            }
        }
    }
    /**
     * Добавление события в историю
     */
    addToHistory(event) {
        this.eventHistory.push(event);
        // Ограничиваем размер истории
        if (this.eventHistory.length > this.maxHistorySize) {
            this.eventHistory = this.eventHistory.slice(-this.maxHistorySize);
        }
    }
    /**
     * Получение истории событий
     */
    getHistory() {
        return [...this.eventHistory];
    }
    /**
     * Получение истории определённого типа
     */
    getHistoryOfType(eventType) {
        return this.eventHistory.filter((event) => event.constructor.name === eventType.name);
    }
    /**
     * Очистка истории событий
     */
    clearHistory() {
        this.eventHistory = [];
    }
    /**
     * Сброс Event Bus
     */
    reset() {
        this.eventHistory = [];
        this.handlers.clear();
        this.eventStream = new Subject();
    }
    /**
     * Получение количества подписчиков для типа события
     */
    getHandlerCount(eventType) {
        const handlers = this.handlers.get(eventType.name);
        return handlers ? handlers.size : 0;
    }
    /**
     * Экспорт истории событий в JSON
     */
    exportHistory() {
        return JSON.stringify(this.eventHistory.map(event => event.toJSON()), null, 2);
    }
    /**
     * Simple string-based event emission (for UI and non-domain events)
     */
    emit(eventName, data) {
        const handlers = this.simpleHandlers.get(eventName);
        if (handlers) {
            handlers.forEach(handler => {
                try {
                    handler(data);
                }
                catch (error) {
                    console.error(`Error in handler for ${eventName}:`, error);
                }
            });
        }
    }
    subscribe(eventNameOrType, handler) {
        if (typeof eventNameOrType === 'string') {
            // String-based subscription
            const eventName = eventNameOrType;
            if (!this.simpleHandlers.has(eventName)) {
                this.simpleHandlers.set(eventName, new Set());
            }
            const handlers = this.simpleHandlers.get(eventName);
            handlers.add(handler);
            // Return unsubscribe function
            return () => {
                handlers.delete(handler);
                if (handlers.size === 0) {
                    this.simpleHandlers.delete(eventName);
                }
            };
        }
        // Original typed subscription - call the original method
        const eventType = eventNameOrType;
        const eventTypeName = eventType.name;
        if (!this.handlers.has(eventTypeName)) {
            this.handlers.set(eventTypeName, new Set());
        }
        const handlers = this.handlers.get(eventTypeName);
        handlers.add(handler);
        const subscription = this.eventStream
            .pipe(filter(event => event.constructor.name === eventTypeName))
            .subscribe(() => { });
        return {
            unsubscribe: () => {
                subscription.unsubscribe();
                handlers.delete(handler);
                if (handlers.size === 0) {
                    this.handlers.delete(eventTypeName);
                }
            },
        };
    }
}
// Singleton instance
let eventBusInstance = null;
/**
 * Получение singleton экземпляра EventBus
 */
export function getEventBus() {
    if (!eventBusInstance) {
        eventBusInstance = new EventBus();
    }
    return eventBusInstance;
}
/**
 * Сброс singleton экземпляра (для тестов)
 */
export function resetEventBus() {
    if (eventBusInstance) {
        eventBusInstance.reset();
    }
    eventBusInstance = null;
}
//# sourceMappingURL=EventBus.js.map